import { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { baueHallOfFameEintrag, ladeSaisonRanking } from '../../utils/hallOfFame';

// Dashboard: Status der gewählten Saison, offene Aufgaben (aus den Daten
// abgeleitet) und die häufigsten Aktionen.
// Schreibt: saisonKonfiguration.deadlinesAktiv / gesperrt (merge),
// hallOfFame/{saison} beim Saisonabschluss; ruft adminNeuberechnungSaison;
// startet die Achievement-Berechnung (Browser).

const TAG_MS = 24 * 60 * 60 * 1000;

function AdminDashboard({ saisonId, saisonName, config, neuLaden, zeigeToast, wechsleTab }) {
  const { userProfile } = useAuth();
  const functions = getFunctions(undefined, 'europe-west3');
  const [laeuft, setLaeuft] = useState(null);
  const [status, setStatus] = useState('');
  const [bestaetigen, setBestaetigen] = useState(null); // 'punkte' | 'achievements' | 'abschluss'
  // Lage der Saison für die Aufgaben: Super Bowl gespielt? Hall of Fame da? Tagesjob gelaufen?
  const [lage, setLage] = useState(null);

  const deadlinesAktiv = config?.deadlinesAktiv !== undefined ? config.deadlinesAktiv : true;
  const gesperrt = config?.gesperrt === true;

  useEffect(() => {
    let aktiv = true;
    const laden = async () => {
      try {
        const [sbSnap, hofSnap, jobSnap, syncSnap] = await Promise.all([
          getDocs(query(collection(db, 'spiele'), where('saison', '==', saisonName), where('playoffRunde', '==', 'superBowl'))),
          getDoc(doc(db, 'hallOfFame', saisonId)),
          getDoc(doc(db, 'systemConfig', 'tagesjob')),
          getDoc(doc(db, 'systemConfig', 'syncStatus'))
        ]);
        if (!aktiv) return;
        setLage({
          superBowlVorhanden: !sbSnap.empty,
          superBowlGespielt: sbSnap.docs.some(d => d.data().completed === true),
          hallOfFame: hofSnap.exists(),
          tagesjob: jobSnap.exists() ? jobSnap.data() : null,
          sync: syncSnap.exists() ? syncSnap.data() : null
        });
      } catch (error) {
        console.error('Lage konnte nicht geladen werden:', error);
        if (aktiv) setLage({ fehler: true });
      }
    };
    laden();
    return () => { aktiv = false; };
  }, [saisonId, saisonName, config]);

  // ---------- Aufgaben aus den Daten ableiten ----------
  const aufgaben = [];
  if (config?.aufgaben?.punktesystemPruefen) {
    aufgaben.push({
      key: 'punkte',
      titel: 'Punktesystem prüfen',
      text: `Die Saison wurde automatisch angelegt, Punktesystem und Bonusfragen stammen aus ${config.kopiertVon || 'der Vorsaison'}. Prüfen, anpassen, speichern – dann verschwindet die Aufgabe.`,
      aktion: () => wechsleTab?.('punkte'),
      knopf: '🎯 Zum Punktesystem'
    });
  }
  const bonusAusgewertet = !!config?.bonusfragenLoesung?.playoffPredictions;
  if (lage?.superBowlGespielt && !bonusAusgewertet && !gesperrt) {
    aufgaben.push({
      key: 'bonus',
      titel: 'Bonusfragen auswerten',
      text: 'Der Super Bowl ist gespielt. Vorschlag laden, MVP eintragen, Probelauf, bestätigen.',
      aktion: () => wechsleTab?.('auswertung'),
      knopf: '🧮 Zur Auswertung'
    });
  }
  if (lage?.superBowlGespielt && bonusAusgewertet && (!lage.hallOfFame || !gesperrt)) {
    aufgaben.push({
      key: 'abschluss',
      titel: 'Saison abschließen',
      text: 'Endstand in die Hall of Fame schreiben und die Saison sperren. Ein Klick, beides zusammen.',
      knopf: '🏛️ Saison abschließen'
    });
  }

  // Score-Update (functions/syncStatus.js → systemConfig/syncStatus):
  // gestört = ESPN liefert gar nichts; Reserve = Daten kommen über eine Rückfall-Abfrage
  const sync = lage?.sync;
  const zeitText = (iso) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const syncGestoert = !!sync?.fehlerZeit && (!sync.okZeit || sync.fehlerZeit > sync.okZeit);
  const syncReserve = !syncGestoert && !!sync?.rueckfallZeit &&
    sync.rueckfallZeit > (sync.primaerOkZeit || '') &&
    (Date.now() - new Date(sync.rueckfallZeit).getTime()) < 2 * TAG_MS;
  const logsOeffnen = () => window.open('https://console.firebase.google.com/project/nfl-tippspiel-3875c/functions/logs', '_blank', 'noopener');
  if (syncGestoert) {
    aufgaben.unshift({
      key: 'sync',
      titel: '⚠️ Score-Update gestört',
      text: `Seit ${zeitText(sync.fehlerZeit)} Uhr kommen keine Ergebnisse an. ${sync.fehler} Die Function versucht es alle 2 Minuten weiter und holt alles nach, sobald ESPN antwortet. Im Log steht das Protokoll jeder Abfrage.`,
      aktion: logsOeffnen,
      knopf: '📜 Logs öffnen'
    });
  } else if (syncReserve) {
    aufgaben.unshift({
      key: 'reserve',
      titel: 'ℹ️ ESPN-Reserve aktiv',
      text: `Ergebnisse kommen an, aber seit ${zeitText(sync.rueckfallZeit)} Uhr nur über die Reserve-Abfrage „${sync.rueckfallStufe}“. Grund: ${sync.rueckfall}. Kein Notfall – aber ESPN hat etwas geändert, die Haupt-Abfrage sollte angepasst werden.`,
      aktion: logsOeffnen,
      knopf: '📜 Logs öffnen'
    });
  }

  // ---------- Aktionen ----------
  const toggleDeadlines = async () => {
    setLaeuft('deadlines');
    try {
      await setDoc(doc(db, 'saisonKonfiguration', saisonId), {
        deadlinesAktiv: !deadlinesAktiv,
        letzteAenderung: new Date().toISOString(),
        geaendertVon: userProfile?.displayName || 'Admin'
      }, { merge: true });
      await neuLaden();
      zeigeToast(`Deadlines ${!deadlinesAktiv ? 'aktiviert' : 'deaktiviert'}`);
    } catch (error) {
      zeigeToast('Fehler: ' + error.message, true);
    } finally {
      setLaeuft(null);
    }
  };

  const saisonAbschliessen = async () => {
    setBestaetigen(null);
    setLaeuft('abschluss');
    setStatus('⏳ Endstand laden…');
    try {
      const ranking = await ladeSaisonRanking(saisonId);
      if (ranking.length === 0) throw new Error('Keine Punkte für diese Saison');
      await setDoc(doc(db, 'hallOfFame', saisonId), baueHallOfFameEintrag(ranking, saisonName, userProfile?.displayName));
      await setDoc(doc(db, 'saisonKonfiguration', saisonId), {
        gesperrt: true,
        letzteAenderung: new Date().toISOString(),
        geaendertVon: userProfile?.displayName || 'Admin'
      }, { merge: true });
      await neuLaden();
      setStatus(`✅ Hall of Fame ${saisonName} gespeichert (${ranking.length} Spieler), Saison gesperrt. Champion-Logo: Reiter Saison.`);
      zeigeToast(`Saison ${saisonName} abgeschlossen`);
    } catch (error) {
      setStatus(`❌ ${error.message}`);
    } finally {
      setLaeuft(null);
    }
  };

  const punkteNeuBerechnen = async () => {
    setBestaetigen(null);
    setLaeuft('punkte');
    setStatus('⏳ Berechne alle Wochen…');
    try {
      const fn = httpsCallable(functions, 'adminNeuberechnungSaison');
      const result = await fn({ saisonId });
      if (result.data.erfolg) {
        setStatus(`✅ ${result.data.anzahlSpiele} Spiele in ${result.data.anzahlWochen} Wochen neu berechnet.`);
        zeigeToast('Punkte neu berechnet');
      } else {
        setStatus(`❌ ${result.data.fehler}`);
      }
    } catch (error) {
      const text = error.code === 'permission-denied' ? 'Keine Admin-Berechtigung' : error.message;
      setStatus(`❌ ${text}`);
    } finally {
      setLaeuft(null);
    }
  };

  const achievementsBerechnen = async () => {
    setBestaetigen(null);
    setLaeuft('achievements');
    setStatus('⏳ Berechne Achievements…');
    try {
      const fn = httpsCallable(functions, 'achievementsBerechnen');
      const r = (await fn({})).data;
      setStatus(`✅ Achievements für ${r.spieler} Spieler geprüft, ${r.geschrieben} aktualisiert.`);
      zeigeToast('Achievements berechnet');
    } catch (error) {
      setStatus(`❌ ${error.message}`);
    } finally {
      setLaeuft(null);
    }
  };

  // ---------- Tagesjob-Status ----------
  const job = lage?.tagesjob;
  const jobAlter = job?.letzterLauf ? Math.floor((Date.now() - new Date(job.letzterLauf + 'T05:00:00+02:00').getTime()) / TAG_MS) : null;
  const jobText = !lage ? '…'
    : !job ? 'noch nie gelaufen (läuft ab dem nächsten Morgen um 5 Uhr)'
      : `${job.letzterLauf}${job.letzteBilanz?.spielplan ? ` · ${job.letzteBilanz.spielplan.neu ?? 0} neu, ${job.letzteBilanz.spielplan.verschoben ?? 0} verschoben` : ''}${job.letzteBilanz?.neueSaison && !/^\d{4}\/\d{2}$/.test(job.letzteBilanz.neueSaison) ? '' : job.letzteBilanz?.neueSaison ? ` · Saison ${job.letzteBilanz.neueSaison} angelegt` : ''}`;
  const jobWarnung = jobAlter !== null && jobAlter >= 2;

  const bestaetigung = (text, onJa) => (
    <span className="bestaetigen">
      {text}
      <button type="button" className="aktion" onClick={onJa}>Ja</button>
      <button type="button" className="aktion aktion--neutral" onClick={() => setBestaetigen(null)}>Nein</button>
    </span>
  );

  return (
    <>
      <div className="kacheln">
        <div className="kachel">
          <div className="kachel__wert" style={{ fontSize: 22 }}>{saisonName}</div>
          <div className="kachel__label">gewählte Saison</div>
        </div>
        <div className="kachel">
          <div className="kachel__wert" style={{ fontSize: 22 }}>{deadlinesAktiv ? '✅' : '⚠️'}</div>
          <div className="kachel__label">Deadlines {deadlinesAktiv ? 'aktiv' : 'AUS'}</div>
        </div>
        <div className="kachel">
          <div className="kachel__wert" style={{ fontSize: 22 }}>{gesperrt ? '🔒' : '🔓'}</div>
          <div className="kachel__label">Saison {gesperrt ? 'gesperrt' : 'offen'}</div>
        </div>
        <div className="kachel">
          <div className="kachel__wert" style={{ fontSize: 22 }}>{jobWarnung ? '⚠️' : job ? '🌙' : '⏳'}</div>
          <div className="kachel__label">Nachtjob {jobWarnung ? `seit ${jobAlter} Tagen still` : job ? 'läuft' : 'wartet'}</div>
        </div>
      </div>

      <div className={`karte${aufgaben.length > 0 ? ' karte--gelb' : ''}`}>
        <h2>📋 Aufgaben</h2>
        {!lage && <p className="karte__hinweis">Prüfe die Lage der Saison…</p>}
        {lage && aufgaben.length === 0 && (
          <p className="karte__hinweis" style={{ marginBottom: 0 }}>
            Nichts offen. Spielplan, Ergebnisse, Punkte und Erinnerungen laufen automatisch.
            {lage.superBowlVorhanden ? '' : ' Die Playoff-Paarungen legt der Nachtjob an, sobald sie feststehen.'}
          </p>
        )}
        {aufgaben.map(a => (
          <div className="zeile" key={a.key}>
            <div className="zeile__text">
              <div className="zeile__label">{a.titel}</div>
              <div className="zeile__hinweis">{a.text}</div>
            </div>
            {a.key === 'abschluss' ? (
              bestaetigen === 'abschluss' ? (
                bestaetigung(`${saisonName} abschließen?`, saisonAbschliessen)
              ) : (
                <button type="button" className="aktion" onClick={() => setBestaetigen('abschluss')} disabled={laeuft !== null}>
                  {laeuft === 'abschluss' ? '⏳ Läuft…' : a.knopf}
                </button>
              )
            ) : (
              <button type="button" className="aktion aktion--neutral" onClick={a.aktion}>{a.knopf}</button>
            )}
          </div>
        ))}
      </div>

      <div className="karte">
        <h2>⚡ Schnellaktionen</h2>
        <div className="zeile">
          <div className="zeile__text">
            <div className="zeile__label">Deadlines</div>
            <div className="zeile__hinweis">Aus = Tipps auch nach Kickoff möglich. Nur für Tests.</div>
          </div>
          <button type="button" className={`schalter-button${deadlinesAktiv ? ' ist-an' : ''}`} onClick={toggleDeadlines} disabled={laeuft === 'deadlines'}>
            {deadlinesAktiv ? 'AN' : 'AUS'}
          </button>
        </div>
        <div className="zeile">
          <div className="zeile__text">
            <div className="zeile__label">Punkte neu berechnen</div>
            <div className="zeile__hinweis">Alle abgeschlossenen Spiele der Saison, per Cloud Function. Dauert bis zu einer Minute.</div>
          </div>
          {bestaetigen === 'punkte' ? (
            bestaetigung('Sicher?', punkteNeuBerechnen)
          ) : (
            <button type="button" className="aktion" onClick={() => setBestaetigen('punkte')} disabled={laeuft !== null}>
              {laeuft === 'punkte' ? '⏳ Läuft…' : '🔄 Neu berechnen'}
            </button>
          )}
        </div>
        <div className="zeile">
          <div className="zeile__text">
            <div className="zeile__label">Achievements berechnen</div>
            <div className="zeile__hinweis">Läuft jede Nacht automatisch. Hier nur, wenn du das Ergebnis sofort sehen willst.</div>
          </div>
          {bestaetigen === 'achievements' ? (
            bestaetigung('Sicher?', achievementsBerechnen)
          ) : (
            <button type="button" className="aktion" onClick={() => setBestaetigen('achievements')} disabled={laeuft !== null}>
              {laeuft === 'achievements' ? '⏳ Läuft…' : '🏅 Berechnen'}
            </button>
          )}
        </div>
        {status && <div className="status-text">{status}</div>}
      </div>

      <div className="karte">
        <h2>🤖 Automatik</h2>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, fontSize: 14 }}>
          <li>Scores und Punkte: Cloud Functions alle 2 Minuten, Erinnerungen alle 30 Minuten.</li>
          <li>Nachtjob um 5 Uhr: Spielplan-Abgleich (Playoff-Paarungen, verschobene Kickoffs), im Frühjahr Anlage der Folgesaison, nach dem Super Bowl Wechsel der aktiven Saison. Letzter Lauf: {jobText}.</li>
          <li>Beim Admin bleiben: Punktesystem der neuen Saison prüfen, Bonusfragen bestätigen, Saison abschließen. Das steht dann oben unter Aufgaben.</li>
          <li>Die Saison oben rechts gilt für alle Reiter.</li>
        </ul>
        <h3 style={{ marginTop: 16 }}>📡 Score-Update</h3>
        <p className="karte__hinweis" style={{ marginBottom: 8 }}>
          {!lage ? '…' : !sync ? 'Noch kein Status geschrieben (kommt mit dem nächsten Lauf zur vollen Stunde).'
            : `Letzter erfolgreicher Abruf: ${sync.okZeit ? zeitText(sync.okZeit) + ' Uhr' : 'noch keiner'}${sync.letzteStufe ? ` über „${sync.letzteStufe}“` : ''}. Abfragen in dieser Reihenfolge: Woche, aktuelle Woche, Kickoff-Tag, Jahr.`}
        </p>
        {sync?.vorfaelle?.length > 0 && (
          <div className="liste">
            {sync.vorfaelle.slice(0, 6).map((v, i) => (
              <div className="liste__zeile" key={i}>
                <span className="liste__links">
                  <span>{v.art === 'fehler' ? '🔴' : v.art === 'rueckfall' ? '🟡' : '🟢'}</span>
                  <span>
                    {v.text}
                    <span className="liste__sub" style={{ display: 'block' }}>{zeitText(v.zeit)} Uhr</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default AdminDashboard;
